import api, { isAuthenticated } from "../utils/api";
import { API_CONFIG } from "../utils/apiConfig";

const authError = {
    success: false,
    error: 'Authentication required. Please log in.',
    status: 401,
    data: null
};

const resolveError = (error, fallback) =>
    error.response?.data?.error || error.response?.data?.message || error.message || fallback;

const toMessage = (data, fallback) => {
    const success = data?.success;
    if (typeof success === 'string') return success;
    return success?.message || fallback;
};

/**
 * Switch the authenticated business user to their personal (mobile) account.
 * @param {string} [personalEmail] - When provided, creates a new personal account with this email (?action=new).
 * @returns {Promise<{success, message?, error?, status, data}>}
 */
export const switchToPersonal = async (personalEmail) => {
    if (!isAuthenticated()) return authError;

    try {
        const response = personalEmail
            ? await api.post(API_CONFIG.USER.SWITCH_TO_PERSONAL, { personalEmail })
            : await api.post(API_CONFIG.USER.SWITCH_TO_PERSONAL);

        return {
            success: true,
            message: toMessage(response.data, 'Switched to personal account successfully'),
            status: response.status,
            data: response.data
        };
    } catch (error) {
        if (error.response?.status === 401) return authError;

        const status = error.response?.status || 500;
        const data = error.response?.data || {};

        // A 409 means the personal account already exists — treat as a successful switch.
        if (status === 409) {
            return {
                success: true,
                message: data.success || 'Personal account already exists',
                status,
                data
            };
        }

        return {
            success: false,
            error: resolveError(error, 'Failed to switch to personal account'),
            status,
            data
        };
    }
};

/**
 * Fetch a personal-app token for the authenticated business user (SSO).
 * The invoice_generator exchanges the business session for a token issued by
 * the personal backend, so the user doesn't need to log in again.
 * @returns {Promise<{success, ssoToken?, user?, personalUrl?, error?, status, data}>}
 */
export const getPersonalToken = async () => {
    if (!isAuthenticated()) return authError;

    try {
        const response = await api.post(API_CONFIG.USER.SWITCH_TO_PERSONAL_TOKEN);

        return {
            success: true,
            ssoToken: response.data?.ssoToken,
            user: response.data?.user,
            personalUrl: response.data?.personalUrl,
            status: response.status,
            data: response.data
        };
    } catch (error) {
        if (error.response?.status === 401) return authError;
        return {
            success: false,
            error: resolveError(error, 'Failed to obtain personal token'),
            status: error.response?.status || 500,
            data: error.response?.data || null
        };
    }
};

/**
 * Switch a personal user to a business account.
 * @param {Object} [payload]
 * @param {string} [payload.businessEmail] - When provided, creates a new business account with this email (?action=new).
 * @param {string} [payload.companyName]
 * @param {string|number} [payload.user_id] - The personal user id to switch (falls back to the authenticated user).
 * @returns {Promise<{success, message?, error?, status, data}>}
 */
export const switchToBusiness = async ({ businessEmail, companyName, user_id } = {}) => {
    if (!isAuthenticated()) return authError;

    const body = {};
    if (businessEmail) body.businessEmail = businessEmail;
    if (companyName) body.companyName = companyName;
    if (user_id) body.user_id = user_id;
    const params = businessEmail ? { action: 'new' } : {};

    try {
        const response = await api.post(API_CONFIG.USER.SWITCH_TO_BUSINESS, body, { params });

        return {
            success: true,
            message: toMessage(response.data, 'Switched to business account successfully'),
            status: response.status,
            data: response.data
        };
    } catch (error) {
        if (error.response?.status === 401) return authError;

        const status = error.response?.status || 500;
        const data = error.response?.data || {};

        // A 409 means the business account already exists — treat as a successful switch.
        if (status === 409) {
            return {
                success: true,
                message: data.success || 'Business account already exists',
                status,
                data
            };
        }

        return {
            success: false,
            error: resolveError(error, 'Failed to switch to business account'),
            status,
            data
        };
    }
};
